import { useState } from "react";
import { FloppyDisk, Plus, Sparkle, Trash } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const META = [
  ["family_name", "Family name", "Sharma"],
  ["company_name", "Company name", "Horizon Mobility Private Limited"],
  ["version_label", "Version", "V0.1"],
  ["status", "Status", "Discussion draft"],
  ["charter_date", "Charter date", "2026-06-20"],
  ["next_review", "Next review", "2026-08-20"],
];

export const CharterBuilder = ({ draft, setDraft, onGenerated }) => {
  const { tenantId } = useAuth();
  const [busy, setBusy] = useState(false);

  const setMeta = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setBody = (key, body) =>
    setDraft((d) => ({ ...d, sections: d.sections.map((s) => (s.key === key ? { ...s, body } : s)) }));

  const payload = () => ({
    family_name: draft.family_name || "",
    company_name: draft.company_name || "",
    version_label: draft.version_label || "V0.1",
    status: draft.status || "Discussion draft",
    charter_date: draft.charter_date || "",
    next_review: draft.next_review || "",
    sections: draft.sections.map(({ key, title, body, hint, mandatory }) => ({
      key, title, body: body || "", hint: hint || "", mandatory: !!mandatory,
    })),
  });

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/tenants/${tenantId}/viveka/charter/draft`, payload());
      toast.success("Draft saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/tenants/${tenantId}/viveka/charter/generate`, payload());
      toast.success(`Charter generated as version ${res.data.version}`);
      onGenerated();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const addCustom = () => {
    const title = window.prompt("Title of the new charter component");
    if (!title?.trim()) return;
    const key = `custom_${Date.now()}`;
    setDraft((d) => ({ ...d, sections: [...d.sections, { key, title: title.trim(), body: "", hint: "Custom component", mandatory: false }] }));
  };

  const removeSection = (key) =>
    setDraft((d) => ({ ...d, sections: d.sections.filter((s) => s.key !== key) }));

  const done = draft.sections.filter((s) => s.mandatory && (s.body || "").trim()).length;
  const total = draft.sections.filter((s) => s.mandatory).length;

  return (
    <div className="space-y-6" data-testid="charter-builder">
      <div className="panel p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="label-caps">Charter header</p>
            <h2 className="font-display text-xl text-navy">Fill the template, then generate</h2>
          </div>
          <p className="rounded-full bg-plum/10 px-4 py-2 text-[11px] uppercase tracking-[0.14em] text-plum"
             data-testid="charter-progress">
            {done} of {total} mandatory components filled
          </p>
        </div>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {META.map(([k, label, ph]) => (
            <label key={k} className="block">
              <span className="label-caps">{label}</span>
              <input value={draft[k] || ""} placeholder={ph} data-testid={`charter-meta-${k}`}
                     onChange={(e) => setMeta(k, e.target.value)}
                     className="mt-2 w-full rounded-xl border border-border bg-card px-3 py-2 text-sm text-navy outline-none focus:border-plum" />
            </label>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        {draft.sections.map((s, i) => (
          <div key={s.key} className="panel overflow-hidden" data-testid={`charter-section-${s.key}`}>
            <div className="flex items-start justify-between gap-3 border-b border-border bg-plum/5 px-5 py-3">
              <div>
                <p className="text-sm font-semibold text-navy">
                  <span className="mr-2 font-mono text-plum">{String(i + 1).padStart(2, "0")}</span>
                  {s.title}
                  {s.mandatory && <span className="ml-2 text-plum">*</span>}
                </p>
                <p className="mt-1 text-[11px] text-muted-foreground">{s.hint}</p>
              </div>
              {!s.mandatory && (
                <button onClick={() => removeSection(s.key)} data-testid={`charter-remove-${s.key}`}
                        className="rounded-full p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                  <Trash size={14} />
                </button>
              )}
            </div>
            <textarea rows={6} value={s.body || ""} data-testid={`charter-input-${s.key}`}
                      onChange={(e) => setBody(s.key, e.target.value)}
                      placeholder="Write in full sentences. Start a line with “-” to make it a bullet."
                      className="w-full resize-y bg-card px-5 py-4 text-sm leading-relaxed text-foreground outline-none" />
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button onClick={addCustom} data-testid="charter-add-section"
                className="inline-flex items-center gap-2 rounded-full border border-navy/25 px-5 py-3 text-[10px] uppercase tracking-[0.16em] text-navy hover:bg-navy hover:text-white">
          <Plus size={14} /> Add component
        </button>
        <button onClick={save} disabled={busy} data-testid="charter-save-draft"
                className="inline-flex items-center gap-2 rounded-full border border-plum/40 px-5 py-3 text-[10px] uppercase tracking-[0.16em] text-plum hover:bg-plum hover:text-white disabled:opacity-50">
          <FloppyDisk size={14} /> Save draft
        </button>
        <button onClick={generate} disabled={busy} data-testid="charter-generate"
                className="inline-flex items-center gap-2 rounded-full bg-plum px-6 py-3 text-[10px] uppercase tracking-[0.16em] text-white hover:bg-plum/90 disabled:opacity-50">
          <Sparkle size={14} /> Submit & generate charter
        </button>
      </div>
    </div>
  );
};
